import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileSignature, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { customerPoliciesAPI } from '../../services/api';

const CustomerPoliciesTab = () => {
  const [policies, setPolicies] = useState([]);
  const [signedIds, setSignedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [acknowledging, setAcknowledging] = useState({});
  const [acknowledged, setAcknowledged] = useState({});

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const res = await customerPoliciesAPI.getMyRequired();
      setPolicies(res.data.policies || []);
      setSignedIds(res.data.signed_policy_ids || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (policy) => {
    if (!acknowledged[policy.id]) {
      toast.error('Please check the acknowledgment box first');
      return;
    }

    setAcknowledging(prev => ({ ...prev, [policy.id]: true }));
    try {
      await customerPoliciesAPI.sign(policy.id);
      toast.success(`"${policy.title}" signed successfully`);
      setSignedIds(prev => [...prev, policy.id]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign policy');
    } finally {
      setAcknowledging(prev => ({ ...prev, [policy.id]: false }));
    }
  };

  const unsignedPolicies = policies.filter(p => !signedIds.includes(p.id));
  const signedPolicies = policies.filter(p => signedIds.includes(p.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Required Documents</h2>
        <p className="text-sm text-[var(--text-muted)]">Please review and sign the following documents</p>
      </div>

      {/* Unsigned Policies Alert */}
      {unsignedPolicies.length > 0 && (
        <div className="p-4 bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Action Required</p>
            <p className="text-sm text-amber-700">
              You have {unsignedPolicies.length} document{unsignedPolicies.length > 1 ? 's' : ''} to review and sign.
            </p>
          </div>
        </div>
      )}

      {/* All Signed */}
      {unsignedPolicies.length === 0 && signedPolicies.length > 0 && (
        <div className="p-4 bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-[var(--success)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[var(--success)]">All Documents Signed</p>
            <p className="text-sm text-green-700">
              You have signed all required documents. Thank you!
            </p>
          </div>
        </div>
      )}

      {/* Unsigned Policies */}
      {unsignedPolicies.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-[var(--text-secondary)]">Pending Documents</h3>
          {unsignedPolicies.map(policy => {
            const isExpanded = expandedPolicy === policy.id;
            
            return (
              <div key={policy.id} className="card overflow-hidden border-2 border-[var(--warning)]/30">
                <div 
                  className="p-4 cursor-pointer hover:bg-[var(--background-subtle)] flex items-center justify-between"
                  onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{policy.title}</h4>
                      <p className="text-xs text-amber-600">Required - Signature needed</p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
                
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    className="border-t"
                  >
                    <div className="p-4 bg-[var(--background-subtle)] max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--text-secondary)]">
                        {policy.content}
                      </pre>
                    </div>
                    <div className="p-4 border-t space-y-4">
                      <label className="flex items-start gap-3 cursor-pointer p-3 bg-[var(--background-paper)] border rounded-lg hover:bg-[var(--background-subtle)]">
                        <Checkbox
                          checked={acknowledged[policy.id] || false}
                          onCheckedChange={(v) => setAcknowledged(prev => ({ ...prev, [policy.id]: v }))}
                          className="mt-0.5"
                        />
                        <span className="text-sm">
                          I have read and understand this document. I agree to abide by these terms and conditions.
                        </span>
                      </label>
                      <Button
                        onClick={() => handleSign(policy)}
                        disabled={!acknowledged[policy.id] || acknowledging[policy.id]}
                        className="w-full btn-primary"
                      >
                        <FileSignature className="w-4 h-4 mr-2" />
                        {acknowledging[policy.id] ? 'Signing...' : 'Sign Document'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Signed Policies */}
      {signedPolicies.length > 0 && (
        <div className="space-y-4 mt-8">
          <h3 className="font-semibold text-[var(--text-secondary)]">Signed Documents</h3>
          {signedPolicies.map(policy => {
            const isExpanded = expandedPolicy === policy.id;
            
            return (
              <div key={policy.id} className="card overflow-hidden opacity-80">
                <div 
                  className="p-4 cursor-pointer hover:bg-[var(--background-subtle)] flex items-center justify-between"
                  onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--success)]/15 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{policy.title}</h4>
                      <p className="text-xs text-[var(--success)]">Signed</p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
                
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    className="border-t"
                  >
                    <div className="p-4 bg-[var(--background-subtle)] max-h-64 overflow-y-auto">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-[var(--text-secondary)]">
                        {policy.content}
                      </pre>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* No Policies */}
      {policies.length === 0 && (
        <div className="card p-12 text-center">
          <CheckCircle className="w-12 h-12 text-[var(--success)] mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Documents Required</h3>
          <p className="text-[var(--text-muted)]">There are no documents requiring your signature at this time.</p>
        </div>
      )}
    </div>
  );
};

export default CustomerPoliciesTab;
